import http from '../../shared/http.js';
import { initLiveList } from '../../shared/liveList.js';
import {
  confirmAction,
  notifyError,
  notifyInfo,
  notifySuccess,
  notifyWarning
} from '../../shared/notify.js';

// The backups page (WOR-LI-B04): the list, plus the two requests it sends to
// the server - a backup now, and for an admin a restore.
//
// Neither request is carried out by the app. It writes the request, the server
// picks it up within a minute and answers beside it, so the page follows the
// request by refetching its own results until the server's answer is in them -
// the same pattern as the job-ad fetch log, and the same rows the page shows
// anyway rather than a second status endpoint.

// How often the results are refetched while a request is open. The server
// looks once a minute and a backup takes seconds, so this is often enough to
// feel prompt without being noise in the server log.
const ABFRAGE_MS = 5000;

// When the page stops waiting. Far beyond a restore - past it the request is
// still answered, the page just stops refreshing on its own.
const HOECHSTENS_MS = 20 * 60 * 1000;

const PFAD = '/sicherungen';

function warte(ms) {
  return new Promise((fertig) => {
    window.setTimeout(fertig, ms);
  });
}

function fehlermeldung(err) {
  return err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
}

// What a finished request did, in the words the toast shows.
function melden(element) {
  if (!element) {
    notifyInfo('Der Auftrag ist abgeschlossen.');
    return;
  }

  const wiederherstellung = element.dataset.auftragArt === 'wiederherstellen';
  const { auftragStatus } = element.dataset;

  if (auftragStatus === 'ok') {
    notifySuccess(
      wiederherstellung ? 'Die Wiederherstellung ist abgeschlossen.' : 'Die Sicherung ist erstellt.'
    );
  } else if (auftragStatus === 'verfallen') {
    notifyWarning('Der Server hat den Auftrag nicht abgeholt.');
  } else {
    notifyError(
      wiederherstellung
        ? 'Die Wiederherstellung ist fehlgeschlagen. Der Grund steht auf der Seite.'
        : 'Die Sicherung ist fehlgeschlagen. Der Grund steht auf der Seite.'
    );
  }
}

export function initSicherungList() {
  const live = initLiveList({
    results: 'sicherung-list',
    path: PFAD,
    forms: ['sicherung-filter']
  });
  if (!live) return;

  const region = document.getElementById('sicherung-list');
  const anlegen = document.getElementById('sicherung-anlegen');

  function offenerAuftrag() {
    return region.querySelector('[data-auftrag-offen="true"]');
  }

  // The same fragment liveList.js fetches, but a request that fails leaves the
  // page alone rather than navigating away. A restore stops the app on
  // purpose, and liveList's fallback - a full navigation to the list URL -
  // would land on the proxy's error page exactly when the page should be
  // saying "wait".
  //
  // Answers whether the region was refreshed, or 'weg' when the session ended
  // and the page is already on its way to the login.
  async function nachladen() {
    try {
      const response = await window.fetch(`${window.location.pathname}${window.location.search}`, {
        headers: { 'X-Fragment': '1' },
        credentials: 'same-origin'
      });
      if (response.redirected && new URL(response.url).pathname !== PFAD) {
        window.location.href = response.url;
        return 'weg';
      }
      if (!response.ok) return false;
      region.innerHTML = await response.text();
      return true;
    } catch {
      return false;
    }
  }

  // Refetches until the request on the page is no longer open, then reports
  // how it ended. Keeps the button disabled meanwhile: a second press would
  // only earn a 409.
  let folgt = false;
  async function begleiteAuftrag() {
    if (folgt) return;
    folgt = true;
    if (anlegen) anlegen.disabled = true;

    const beginn = Date.now();
    let unterbrochen = false;

    try {
      const id = offenerAuftrag()?.dataset.auftragId;

      while (offenerAuftrag() && Date.now() - beginn < HOECHSTENS_MS) {
        await warte(ABFRAGE_MS);
        const ergebnis = await nachladen();
        if (ergebnis === 'weg') return;
        // Said once, the first time the app does not answer: during a restore
        // that is the expected state, not a fault.
        if (!ergebnis && !unterbrochen) {
          unterbrochen = true;
          notifyInfo(
            'Die Anwendung ist kurz nicht erreichbar. Die Seite meldet sich, sobald sie zurück ist.'
          );
        }
      }

      if (offenerAuftrag()) {
        notifyInfo('Der Auftrag läuft noch. Die Seite aktualisiert sich nicht mehr selbst.');
        return;
      }

      melden(id ? region.querySelector(`[data-auftrag-id="${id}"]`) : null);
    } finally {
      folgt = false;
      if (anlegen) anlegen.disabled = false;
    }
  }

  // After a request was accepted, or refused because another is open: show
  // whichever is current and follow it.
  async function zeigeUndBegleite() {
    await nachladen();
    begleiteAuftrag();
  }

  // No confirmation: a backup changes nothing and costs nothing, and the button
  // says what it does.
  anlegen?.addEventListener('click', async () => {
    anlegen.disabled = true;
    try {
      const { data } = await http.post('/sicherungen');
      notifyInfo(data.data.hinweis);
      await zeigeUndBegleite();
    } catch (err) {
      notifyError(fehlermeldung(err));
      if (err.response?.status === 409) await zeigeUndBegleite();
      else anlegen.disabled = false;
    }
  });

  // The restore buttons arrive with every refreshed fragment, so the listener
  // sits on the region rather than on each button.
  region.addEventListener('click', async (event) => {
    const knopf = event.target.closest('[data-wiederherstellen]');
    if (!knopf) return;

    const { wiederherstellen: datei, zeitpunkt, dokumente } = knopf.dataset;
    const bestaetigt = await confirmAction({
      title: 'Sicherung wiederherstellen?',
      // Spelled out in full, because this is the one button in the application
      // that replaces every record at once: what is lost, what is kept, and
      // that the application goes away for a moment.
      message: [
        `Alle Daten der Anwendung werden auf den Stand vom ${zeitpunkt} zurückgesetzt.`,
        'Alles, was seither erfasst oder geändert wurde, geht verloren.',
        dokumente === 'true'
          ? 'Gelöschte Dokumente kommen zurück, neuere bleiben erhalten.'
          : 'Zu dieser Sicherung gibt es kein Dokumenten-Archiv - die Dokumente bleiben, wie sie sind.',
        'Der heutige Stand wird vorher gesichert. Die Anwendung ist dabei einige Minuten nicht erreichbar.'
      ].join(' '),
      okText: 'Wiederherstellen'
    });
    if (!bestaetigt) return;

    try {
      const { data } = await http.post('/sicherungen/wiederherstellen', { datei });
      notifyInfo(data.data.hinweis);
      await zeigeUndBegleite();
    } catch (err) {
      notifyError(fehlermeldung(err));
      if (err.response?.status === 409) await zeigeUndBegleite();
    }
  });

  // A request already open when the page loads - from another tab, or from
  // somebody else - is followed the same way.
  if (offenerAuftrag()) begleiteAuftrag();
}
